import type {
  MotionChoice,
  MotionColors,
  MotionDesignOptions,
  MotionDirection,
  MotionPaletteChoice,
  MotionPaletteRole,
  MotionPreferences,
  ReelMotionDesign,
} from './types';

/*
 * Motion design as the Studio server offers it (GET /options `motionDesign`): the looks,
 * motion amounts, colors and sound effects a job can ask for, and the changes a reel with a
 * design takes. A server that offers none, or a schema this client does not know, shows no
 * motion control and is sent no motion field. Seeds, catalog versions and recipe ids stay the
 * server's: authors see only the server's words for each choice.
 */

/** The motion design schema this client reads; any other hides the motion controls. */
export const MOTION_SCHEMA_VERSION = 1;

const DIRECTIONS: readonly MotionDirection[] = ['auto', 'family', 'locked'];
const COLOR_POLICIES: readonly MotionColors[] = ['auto', 'brand', 'palette'];

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
/** A #rrggbb color, the only kind a swatch is drawn with. */
export const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_COLOR.test(value);

const text = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

/** The whole choices of a list, once each, in the server's order; `known` limits their ids. */
function readChoices<T extends string>(
  value: unknown,
  known?: readonly T[],
): MotionChoice<T>[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const choices: MotionChoice<T>[] = [];
  for (const item of value) {
    const {id, label, hint} = record(item);
    if (!text(id) || !text(label) || seen.has(id)) continue;
    if (known && !known.includes(id as T)) continue;
    seen.add(id);
    choices.push({id: id as T, label, ...(text(hint) ? {hint} : {})});
  }
  return choices;
}

/** The palettes with an id, a name and at least one #rrggbb swatch. */
function readPalettes(value: unknown): MotionPaletteChoice[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const palettes: MotionPaletteChoice[] = [];
  for (const item of value) {
    const {id, label, swatches} = record(item);
    const colors = Array.isArray(swatches) ? swatches.filter(isHexColor) : [];
    if (!text(id) || !text(label) || seen.has(id) || colors.length === 0)
      continue;
    seen.add(id);
    palettes.push({id, label, swatches: colors});
  }
  return palettes;
}

/** The server's default when it offers it, or else the first choice. */
const offered = <T extends string>(
  choices: MotionChoice<T>[],
  value: unknown,
) => choices.find(choice => choice.id === value)?.id ?? choices[0].id;

/**
 * The motion choices a server offers, keeping only whole ones; null when it offers none, or
 * offers a schema or lists this client cannot use. A style family can be asked for only when
 * there are families, and a palette only when there are palettes.
 */
export function readMotionDesign(value: unknown): MotionDesignOptions | null {
  const design = record(value);
  if (design.schemaVersion !== MOTION_SCHEMA_VERSION) return null;
  const families = readChoices(design.families);
  const palettes = readPalettes(design.palettes);
  const directions = readChoices(design.directions, DIRECTIONS).filter(
    direction => direction.id !== 'family' || families.length > 0,
  );
  const colorPolicies = readChoices(
    design.colorPolicies,
    COLOR_POLICIES,
  ).filter(policy => policy.id !== 'palette' || palettes.length > 0);
  const amounts = readChoices(design.amounts);
  const soundModes = readChoices(design.soundModes);
  if (
    !directions.length ||
    !amounts.length ||
    !colorPolicies.length ||
    !soundModes.length
  )
    return null;
  const defaults = record(design.defaults);
  const actions = record(design.actions);
  return {
    schemaVersion: MOTION_SCHEMA_VERSION,
    catalogVersion: text(design.catalogVersion) ? design.catalogVersion : '',
    directions,
    families,
    amounts,
    colorPolicies,
    palettes,
    soundModes,
    defaults: {
      direction: offered(directions, defaults.direction),
      amount: offered(amounts, defaults.amount),
      colors: offered(colorPolicies, defaults.colors),
      sound: offered(soundModes, defaults.sound),
    },
    actions: {
      reelLook: actions.reelLook === true,
      sceneAnimation: actions.sceneAnimation === true,
      lock: actions.lock === true,
      settings: actions.settings === true,
    },
  };
}

/** The create form's motion choices, before they become the brief's. */
export interface MotionChoices {
  direction: MotionDirection;
  /** The style family: needed for 'family'; for 'locked', '' leaves the choice to Studio. */
  family: string;
  amount: string;
  colors: MotionColors;
  /** The chosen palette, for colors 'palette'. */
  paletteId: string;
  sound: string;
}

/** A new form starts from the server's defaults. */
export const defaultMotionChoices = (
  design: MotionDesignOptions,
): MotionChoices => ({
  direction: design.defaults.direction,
  family: '',
  amount: design.defaults.amount,
  colors: design.defaults.colors,
  paletteId: '',
  sound: design.defaults.sound,
});

/** The family the form shows for a direction: a family direction always has one. */
export function chosenFamily(
  design: MotionDesignOptions,
  {direction, family}: Pick<MotionChoices, 'direction' | 'family'>,
) {
  if (design.families.some(choice => choice.id === family)) return family;
  return direction === 'family' ? design.families[0]?.id ?? '' : '';
}

/** The palette the form shows: choosing a palette always has one. */
export const chosenPalette = (
  design: MotionDesignOptions,
  {paletteId}: Pick<MotionChoices, 'paletteId'>,
) =>
  design.palettes.some(palette => palette.id === paletteId)
    ? paletteId
    : design.palettes[0]?.id ?? '';

/**
 * The brief's motion choices. A family is sent only for a family direction, or a kept
 * direction with a chosen family; a palette only when the colors are a chosen palette.
 */
export function motionPreferences(
  design: MotionDesignOptions,
  choices: MotionChoices,
): MotionPreferences {
  const offers = <T extends string>(list: MotionChoice<T>[], id: string) =>
    list.some(choice => choice.id === id);
  const direction = offers(design.directions, choices.direction)
    ? choices.direction
    : design.defaults.direction;
  const colors = offers(design.colorPolicies, choices.colors)
    ? choices.colors
    : design.defaults.colors;
  const family = chosenFamily(design, {direction, family: choices.family});
  const paletteId = chosenPalette(design, choices);
  return {
    direction,
    ...(direction !== 'auto' && family ? {family} : {}),
    amount: offers(design.amounts, choices.amount)
      ? choices.amount
      : design.defaults.amount,
    colors,
    ...(colors === 'palette' && paletteId ? {paletteId} : {}),
    sound: offers(design.soundModes, choices.sound)
      ? choices.sound
      : design.defaults.sound,
  };
}

/** A choice's name in the server's words; undefined for one it does not list. */
export const choiceLabel = (
  choices: {id: string; label: string}[],
  id: string | null | undefined,
) => choices.find(choice => choice.id === id)?.label;

/** The colors of a reel's palette a swatch shows, in this order, when they are #rrggbb. */
const SWATCH_ROLES: MotionPaletteRole[] = [
  'background',
  'surface',
  'primaryAccent',
  'secondaryAccent',
  'text',
];

/** The colors that show a reel's look, once each. */
export function reelSwatches(motion: ReelMotionDesign): string[] {
  const colors = SWATCH_ROLES.map(role => motion.palette?.[role]).filter(
    isHexColor,
  );
  return colors.filter(
    (color, index) =>
      colors.findIndex(other => other.toLowerCase() === color.toLowerCase()) ===
      index,
  );
}

/** A picture of a palette's swatches, side by side, for its choice card. */
export function swatchImage(swatches: string[]) {
  const colors = swatches.filter(isHexColor);
  if (!colors.length) return undefined;
  const width = 64 / colors.length;
  const bands = colors
    .map(
      (color, index) =>
        `<rect x="${index * width}" y="0" width="${
          width + 0.5
        }" height="64" fill="${color}"/>`,
    )
    .join('');
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">${bands}</svg>`,
  )}`;
}
